import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef
} from 'react';
import mapboxgl, { MarkerOptions } from 'mapbox-gl';
import MapboxGeocoder, {
  GeocoderOptions,
  Results,
  Result
} from '@mapbox/mapbox-gl-geocoder';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import clsx from '../clsx';
import { coordinatesGeocoder, updatePlaceName } from './Geocoder.helpers';
import styles from './Geocoder.module.scss';

import { getAccessToken } from './utils';

// TODO: look into adding this to `@types/mapbox__mapbox-gl-geocoder`
export interface LoadingEvent {
  query: string;
}

// TODO: look into adding this to `@types/mapbox__mapbox-gl-geocoder`
export interface ResultsEvent extends Results {}

// TODO: look into adding this to `@types/mapbox__mapbox-gl-geocoder`
export interface ResultEvent {
  result: Result;
}

// TODO: look into adding this to `@types/mapbox__mapbox-gl-geocoder`
export interface ErrorEvent {
  error: string;
}

export interface GeocoderInstance extends MapboxGeocoder {
  // TODO: once this are added to `@types/mapbox__mapbox-gl-geocoder` remove them here
  setAutocomplete(value: boolean): this;
  getAutocomplete(): boolean;
  setFuzzyMatch(value: boolean): this;
  getFuzzyMatch(): boolean;
  setRouting(value: boolean): this;
  getRouting(): boolean;
  setWorldview(code: string): this;
  getWorldview(): string;
}

export interface GeocoderProps
  extends Omit<GeocoderOptions, 'accessToken' | 'marker'> {
  /**
   * Mapbox API token
   */
  apiToken?: string;

  /**
   * mapbox-gl instance / ref
   */
  mapboxRef?: mapboxgl.Map;

  /**
   * Unique ID for the geocoder - if not set will add a unique ID internally
   */
  id?: string;

  /**
   * Class name for the geocoder container
   */
  className?: string;

  /**
   * Called when the input is cleared
   */
  onClear?: () => void;

  /**
   * Called when the geocoder is looking up a query
   */
  onLoading?: (e: LoadingEvent) => void;

  /**
   * Called when the geocoder returns a response while user types into the input field
   */
  onResults?: (e: ResultsEvent) => void;

  /**
   * Called when suggestion is selected from the list
   */
  onResult?: (e: ResultEvent) => void;

  /**
   * Called when geocoder encounters an error
   */
  onError?: (e: ErrorEvent) => void;

  // TODO: once the following options types are added to
  // `@types/mapbox__mapbox-gl-geocoder` remove them here

  /**
   * Specify whether to return autocomplete results or not. When autocomplete is enabled,
   * results will be included that start with the requested string, rather than just responses
   * that match it exactly. (optional, default true)
   */
  autocomplete?: boolean;
  /**
   *  Specify whether the Geocoding API should attempt approximate, as well as exact, matching
   *  when performing searches, or whether it should opt out of this behavior and only attempt
   *  exact matching. (optional, default true)
   */
  fuzzyMatch?: boolean;
  /**
   * Specify whether to request additional metadata about the recommended navigation
   * destination corresponding to the feature or not. Only applicable for address features.
   * (optional, default false)
   */
  routing?: boolean;
  /**
   * Filter results to geographic features whose characteristics are defined differently by
   * audiences belonging to various regional, cultural, or political groups. (optional,
   * default "us")
   */
  worldview?: string;

  /*
   * The mapbox-gl-geocoder expects an object of type MarkerOptions, but the type declaration for
   * mapbox-gl-geocoder specifies that it shold be an instance of `mapboxgl.Marker` which is wrong.
   * **/
  marker?: boolean | MarkerOptions;
}

let geocoderIndex = 1;

const Geocoder = forwardRef<GeocoderInstance, GeocoderProps>((props, ref) => {
  const {
    apiToken,
    mapboxRef,
    id: idProp,
    className,
    // TODO: look into removing this option as `options` is not something supported natively in MapboxGeocoder
    // @ts-expect-error for backwards compatibility - once active is updated to not use `options` we can remove it
    options,
    // All the props that will be passed to the MapboxGeocoder instance
    ...geocoderProps
  } = props;
  const propsRef = useRef(props);
  propsRef.current = props;

  // Each geocoder needs to have a unique ID, so we when the id props is not passed in we generate one.
  //
  // eslint-disable-next-line no-plusplus
  const id = useMemo(() => idProp || `geocoder-${geocoderIndex++}`, [idProp]);

  const geocoder = useMemo(
    () => {
      return new MapboxGeocoder({
        accessToken: getAccessToken(apiToken),
        mapboxgl,
        flyTo: {
          animate: false
        },
        marker: false,
        reverseGeocode: true,
        localGeocoder: coordinatesGeocoder,
        ...geocoderProps,
        ...options
      }) as GeocoderInstance;
    },
    // As we only want to create mapboxgl.Marker once and as we manually update it's props/options,
    // we intentionally have empty dependency array and disabled the eslint rule here.
    //
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useImperativeHandle(ref, () => geocoder, [geocoder]);

  useEffect(() => {
    const onClear = () => {
      propsRef.current?.onClear?.();
    };
    geocoder.on('clear', onClear);

    const onLoading = (e: LoadingEvent) => {
      propsRef.current?.onLoading?.(e);
    };
    geocoder.on('loading', onLoading);

    // Called while the user types into the input field
    const onResults = (e: ResultsEvent) => {
      propsRef.current?.onResults?.(e);
    };
    geocoder.on('results', onResults);

    // Called when suggestion is selected from the list
    const onResult = ({ result }: ResultEvent) => {
      propsRef.current?.onResult?.({
        result: {
          ...result,
          place_name: updatePlaceName(result.place_name)
        }
      });
    };
    geocoder.on('result', onResult);

    const onError = (e: ErrorEvent) => {
      propsRef.current?.onError?.(e);
    };
    geocoder.on('error', onError);

    // This will also work in cases where the mapboxRef is null due to the Geocoder being rendered without a map.
    document
      ?.getElementById(id)
      ?.appendChild(geocoder.onAdd(mapboxRef as mapboxgl.Map));

    return () => {
      geocoder.off('clear', onClear);
      geocoder.off('loading', onLoading);
      geocoder.off('results', onResults);
      geocoder.off('result', onResult);
      geocoder.off('error', onError);
      geocoder.onRemove();
    };
  }, [mapboxRef, geocoder, id]);

  if (
    props.proximity !== undefined &&
    props.proximity !== geocoder.getProximity()
  ) {
    geocoder.setProximity(props.proximity);
  }

  if (
    props.render !== undefined &&
    props.render !== geocoder.getRenderFunction()
  ) {
    geocoder.setRenderFunction(props.render);
  }

  if (
    props.language !== undefined &&
    props.language !== geocoder.getLanguage()
  ) {
    geocoder.setLanguage(props.language);
  }

  if (props.zoom !== undefined && props.zoom !== geocoder.getZoom()) {
    geocoder.setZoom(props.zoom);
  }

  if (props.flyTo !== undefined && props.flyTo !== geocoder.getFlyTo()) {
    geocoder.setFlyTo(props.flyTo);
  }

  if (
    props.placeholder !== undefined &&
    props.placeholder !== geocoder.getPlaceholder()
  ) {
    geocoder.setPlaceholder(props.placeholder);
    // @ts-expect-error Accessing private property that is not exposed in types to get
    // around a bug in the library, once the bug is fixed we can remove this line.
    geocoder.options.placeholder = props.placeholder;
  }

  if (props.bbox !== undefined && props.bbox !== geocoder.getBbox()) {
    geocoder.setBbox(props.bbox);
  }
  if (
    props.countries !== undefined &&
    props.countries !== geocoder.getCountries()
  ) {
    geocoder.setCountries(props.countries);
  }

  if (props.types !== undefined && props.types !== geocoder.getTypes()) {
    geocoder.setTypes(props.types);
  }

  if (
    props.minLength !== undefined &&
    props.minLength !== geocoder.getMinLength()
  ) {
    geocoder.setMinLength(props.minLength);
  }

  if (props.limit !== undefined && props.limit !== geocoder.getLimit()) {
    geocoder.setLimit(props.limit);
  }

  if (props.filter !== undefined && props.filter !== geocoder.getFilter()) {
    geocoder.setFilter(props.filter);
  }

  if (props.origin !== undefined && props.origin !== geocoder.getOrigin()) {
    geocoder.setOrigin(props.origin);
  }

  if (props.autocomplete && props.autocomplete !== geocoder.getAutocomplete()) {
    geocoder.setAutocomplete(props.autocomplete);
  }

  if (
    props.fuzzyMatch !== undefined &&
    props.fuzzyMatch !== geocoder.getFuzzyMatch()
  ) {
    geocoder.setFuzzyMatch(props.fuzzyMatch);
  }

  if (props.routing !== undefined && props.routing !== geocoder.getRouting()) {
    geocoder.setRouting(props.routing);
  }

  if (
    props.worldview !== undefined &&
    props.worldview !== geocoder.getWorldview()
  ) {
    geocoder.setWorldview(props.worldview);
  }

  return <div id={id} className={clsx(styles.geocoder, className)} />;
});

export default Geocoder;
